import { lazy, useEffect, useState, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Spinner } from '@wrenchlab-ui/components/ui/Spinner'
import { getRememberedTechnicianSession, useAuthStore } from '@wrenchlab-ui/store/authStore'
import { authApi } from '@wrenchlab-ui/api/authApi'
import LoginPage from '@wrenchlab-ui/pages/LoginPage'
import SessionLimitPage from '@wrenchlab-ui/pages/SessionLimitPage'
import StaffLoginPage from '@wrenchlab-ui/pages/StaffLoginPage'
import WorkspaceLoginPage from '@wrenchlab-ui/pages/WorkspaceLoginPage'
import TechnicianLoginPage from '@wrenchlab-ui/pages/TechnicianLoginPage'
import AppShell from '@wrenchlab-ui/components/layout/AppShell'
import { TechnicianLayout } from '@wrenchlab-ui/layouts/TechnicianLayout'
import { AccountGate } from '@wrenchlab-ui/components/AccountGate'
import { MyJobsPage } from '@wrenchlab-ui/pages/technician/MyJobsPage'
import { JobDetailPage } from '@wrenchlab-ui/pages/technician/JobDetailPage'
import { MessagesPage } from '@wrenchlab-ui/pages/technician/MessagesPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'

// All pages are lazy-loaded; AppShell's Suspense boundary handles the spinner.
const DashboardPage     = lazy(() => import('@wrenchlab-ui/pages/DashboardPage'))
const CustomersPage     = lazy(() => import('@wrenchlab-ui/pages/CustomersPage'))
const VehiclesPage      = lazy(() => import('@wrenchlab-ui/pages/VehiclesPage'))
const WorkOrdersPage    = lazy(() => import('@wrenchlab-ui/pages/WorkOrdersPage'))
const InvoicesPage      = lazy(() => import('@wrenchlab-ui/pages/InvoicesPage'))
const InventoryPage     = lazy(() => import('@wrenchlab-ui/pages/InventoryPage'))
const StaffPage         = lazy(() => import('@wrenchlab-ui/pages/StaffPage'))
const IntakePage        = lazy(() => import('@wrenchlab-ui/pages/IntakePage'))
const CheckInQRPage     = lazy(() => import('@wrenchlab-ui/pages/CheckInQRPage'))
const TechnicianAccessQRPage = lazy(() => import('@wrenchlab-ui/pages/TechnicianAccessQRPage'))
const WorkBoardPage     = lazy(() => import('@wrenchlab-ui/pages/WorkBoardPage'))
const ReportsPage       = lazy(() => import('@wrenchlab-ui/pages/ReportsPage'))
const PerformancePage   = lazy(() => import('@wrenchlab-ui/pages/PerformancePage'))
const NotificationsPage = lazy(() => import('@wrenchlab-ui/pages/NotificationsPage'))
// SettingsPage shows CompanySettingsForm only — StorageModeToggle is hidden for clientType='Web'
const SettingsPage      = lazy(() => import('@wrenchlab-ui/pages/SettingsPage'))
const ApiKeysPage       = lazy(() => import('@wrenchlab-ui/pages/ApiKeysPage'))
const IntegrationsPage  = lazy(() => import('@wrenchlab-ui/pages/IntegrationsPage'))
const BroadcastPage     = lazy(() => import('@wrenchlab-ui/pages/BroadcastPage'))
const EstimatesPage     = lazy(() => import('@wrenchlab-ui/pages/EstimatesPage'))
const AppointmentsPage  = lazy(() => import('@wrenchlab-ui/pages/AppointmentsPage'))
const RemindersPage     = lazy(() => import('@wrenchlab-ui/pages/RemindersPage'))
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token)
  const hasHydrated = useAuthStore((s) => s._hasHydrated)
  if (!hasHydrated) return <div className="flex h-screen items-center justify-center"><Spinner /></div>
  if (!token) return <Navigate to="/workspace-login" replace />
  return <>{children}</>
}

function RequireTechnicianAuth({ children }: { children: React.ReactNode }) {
  const hasHydrated = useAuthStore((s) => s._hasHydrated)
  const token = useAuthStore((s) => s.token)
  const staffRole = useAuthStore((s) => s.staffRole)
  const [recovered, setRecovered] = useState(false)
  useEffect(() => {
    if (!hasHydrated) return
    if (!(token && staffRole && isTechnicianPortalRole(staffRole))) {
      const saved = getRememberedTechnicianSession()
      if (saved) { const auth = useAuthStore.getState(); auth.setAuth(saved.token, saved.tenantSlug, ''); auth.setStaffSession(saved.token, saved.staffId, saved.staffRole, saved.staffName) }
    }
    setRecovered(true)
  }, [hasHydrated, token, staffRole])
  if (!hasHydrated || !recovered) return <div className="flex h-screen items-center justify-center"><Spinner /></div>
  const current = useAuthStore.getState()
  if (!current.token || !isTechnicianPortalRole(current.staffRole ?? '')) return <Navigate to="/technician-login" replace />
  return <>{children}</>
}

function TechnicianRoleGuard({ children }: { children: React.ReactNode }) {
  const staffRole = useAuthStore((s) => s.staffRole)
  if (!staffRole) return <Navigate to="/staff-login" replace />
  if (!isTechnicianPortalRole(staffRole)) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function ManagementRoleGuard({ children }: { children: React.ReactNode }) {
  const staffRole = useAuthStore((s) => s.staffRole)
  if (staffRole && isTechnicianPortalRole(staffRole)) return <Navigate to="/tech" replace />
  return <>{children}</>
}

const isTechnicianPortalRole = (role: string) => ['Technician', 'SeniorTechnician', 'Apprentice', 'Student', 'Intern'].includes(role)

export default function App() {
  const setClientType = useAuthStore((s) => s.setClientType)
  const token = useAuthStore((s) => s.token)
  const sessionId = useAuthStore((s) => s.sessionId)

  // Ensure clientType is always 'Web', even if zustand persisted a different value
  useEffect(() => { setClientType('Web') }, [setClientType])

  useEffect(() => {
    if (!token || !sessionId) return

    const sendHeartbeat = () => {
      authApi.heartbeat().catch(() => {})
    }

    sendHeartbeat()
    const id = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [token, sessionId])

  const pageFallback = <div className="flex h-screen items-center justify-center"><Spinner /></div>

  return (
    <Suspense fallback={pageFallback}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/workspace-login" element={<WorkspaceLoginPage />} />
      <Route path="/technician-login" element={<TechnicianLoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/session-limit" element={<SessionLimitPage />} />
      <Route
        path="/staff-login"
        element={
          <RequireAuth>
            <StaffLoginPage />
          </RequireAuth>
        }
      />
      {/* Technician Workspace Routes */}
      <Route
        path="/tech"
        element={
          <RequireTechnicianAuth>
            <AccountGate>
              <TechnicianRoleGuard>
                <TechnicianLayout />
              </TechnicianRoleGuard>
            </AccountGate>
          </RequireTechnicianAuth>
        }
      >
        <Route index element={<MyJobsPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="job/:workOrderId" element={<JobDetailPage />} />
        <Route path="*" element={<Navigate to="/tech" replace />} />
      </Route>
      <Route
        path="/*"
        element={
          <RequireAuth>
            <AccountGate>
              <ManagementRoleGuard><AppShell /></ManagementRoleGuard>
            </AccountGate>
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard"     element={<DashboardPage />} />
        <Route path="customers"     element={<CustomersPage />} />
        <Route path="vehicles"      element={<VehiclesPage />} />
        <Route path="workorders"    element={<WorkOrdersPage />} />
        <Route path="invoices"      element={<InvoicesPage />} />
        <Route path="inventory"     element={<InventoryPage />} />
        <Route path="staff"         element={<StaffPage />} />
        <Route path="intake"        element={<IntakePage />} />
        <Route path="work-board"    element={<WorkBoardPage />} />
        <Route path="checkin-qr"    element={<CheckInQRPage />} />
        <Route path="technician-access" element={<TechnicianAccessQRPage />} />
        <Route path="reports"       element={<ReportsPage />} />
        <Route path="performance"   element={<PerformancePage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings"      element={<SettingsPage />} />
        <Route path="api-keys"      element={<ApiKeysPage />} />
        <Route path="integrations"  element={<IntegrationsPage />} />
        <Route path="broadcast"     element={<BroadcastPage />} />
        <Route path="estimates"     element={<EstimatesPage />} />
        <Route path="appointments"  element={<AppointmentsPage />} />
        <Route path="reminders"     element={<RemindersPage />} />
        <Route path="*"             element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
    </Suspense>
  )
}
